import React, { useEffect, useState } from 'react';

import { styled } from '@mui/material/styles';
import { Map, useApiIsLoaded, useMap } from '@vis.gl/react-google-maps';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';

import {
    DEFAULT_LAT,
    DEFAULT_LNG,
    DEFAULT_MAP_PREVIEW_ZOOM,
} from 'common/constants';

const StyledMap = styled('div')`
    align-items: center;
    background-color: #c5c5c5;
    bottom: 0;
    display: flex;
    height: ${({ heightMap }) => (heightMap ? heightMap : '125px')};
    justify-content: center;
    left: 0;
    opacity: ${({ disabled }) => (disabled ? '.8' : '1')};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'auto')};
    position: relative;
    right: 0;
    top: 0;
`;

const MapContainer = ({
    center = { lat: DEFAULT_LAT, lng: DEFAULT_LNG },
    handleOnPositionChanged,
    heightMap,
    mapType = 'roadmap',
    marker = {},
    readOnly,
    zoom = DEFAULT_MAP_PREVIEW_ZOOM,
    streetViewControl = false,
}) => {
    const [google, setGoogle] = useState(null);
    const [mapMarker, setMapMarker] = useState(null);

    const apiIsLoaded = useApiIsLoaded();
    const map = useMap();

    const onDrag = (e) => {
        const latlng = new google.maps.LatLng(
            e.map.center.lat(),
            e.map.center.lng(),
        );
        mapMarker.position = latlng;
    };

    const onDragend = (e) => {
        if (handleOnPositionChanged) handleOnPositionChanged(null, e.map);
    };

    const onZoomChanged = (e) => {
        if (handleOnPositionChanged) handleOnPositionChanged(null, e.map);
    };

    useEffect(() => {
        if (!apiIsLoaded) return;
        setGoogle(window.google);
    }, [apiIsLoaded]);

    useEffect(() => {
        if (!map || !google || isEmpty(marker) || mapMarker) return;
        const position = new google.maps.LatLng(marker.lat, marker.lng);
        const newMarker = new google.maps.marker.AdvancedMarkerElement({
            position,
            map,
        });
        setMapMarker(newMarker);
    }, [google, map]);

    useEffect(() => {
        if (!mapMarker || !google || !map || isEmpty(center)) return;
        mapMarker.position = new google.maps.LatLng(center.lat, center.lng);
        map.setCenter(mapMarker.position);
    }, [center, google, map, mapMarker]);

    if (isEmpty(center)) return null;

    return (
        <StyledMap disabled={readOnly} heightMap={heightMap}>
            <Map
                defaultCenter={{ lat: +center.lat, lng: +center.lng }}
                defaultZoom={zoom}
                mapId={import.meta.env.VITE_GOOGLE_MAPS_DEFAULT_ID}
                mapTypeId={mapType}
                onDrag={onDrag}
                onDragend={onDragend}
                onZoomChanged={onZoomChanged}
                streetViewControl={streetViewControl}
            />
        </StyledMap>
    );
};

MapContainer.propTypes = {
    center: PropTypes.object,
    handleOnPositionChanged: PropTypes.func,
    heightMap: PropTypes.string,
    mapType: PropTypes.string,
    marker: PropTypes.object,
    readOnly: PropTypes.bool,
    streetViewControl: PropTypes.bool,
    zoom: PropTypes.number,
};

export default MapContainer;
